import React, { FC, MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownloadIconCont,
  NoReport,
  ReportGroup,
  ReportsNestedTableTileCont,
  ReportsNestedTableTileContentCont,
  ReportsRegularNestedTableTileEl,
  ReportsLowercaseNestedTableTileEl,
  ReportTileCont,
  TooltipContainer,
  TooltipList,
  TooltipContent,
} from './ReportsNestedTableTile.styles';
import { RouteNames, RoleNames } from '../../../../constans/routes';
import Tooltip from '../../../../HOC/Tooltip/Tooltip';
import { useAuth } from '../../../../hooks/useAuth';
import { useReports } from '../../../../hooks/useReports';
import { IItemReportResponse } from '../../../../services/reports';
import { IShareUser } from '../../../../services/surveys';
import DownloadReportModal from '../../../DownloadReportModal/DownloadReportModal';
import ResponseRate from '../../../ResponseRate/ResponseRate';
import DownloadIcon from '@/assets/icons/download.svg?react';

interface IReportsNestedTableTile {
  item: IItemReportResponse;
  surveyType: string;
}
const ReportsNestedTableTile: FC<IReportsNestedTableTile> = ({ item, surveyType }) => {
  const { t } = useTranslation();
  const { id, group, type, responsiveness, accessList, is_anonymized } = item;
  const [downloadIconVis, setDownloadIconVis] = useState(false);
  const { getReportType } = useReports();
  const { userRolesFromToken, isEBHRMember } = useAuth();

  const users = accessList?.users ?? [];

  const accessUsersString = useMemo(
    () => users.map(u => u?.name || u?.email || ' - ').join(', '),
    [users]
  );

  const accessUsersList = useMemo(
    () => users.map(u => <div key={u?.name || u?.email}>{u?.name || u?.email || ' - '}</div>),
    [users]
  );

  const showAccessTooltip = users.length > 3;

  const handleRedirectPrevent = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!is_anonymized) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const hideAccess = !isEBHRMember && userRolesFromToken.roles?.includes(RoleNames.Manager);

  return (
    <ReportTileCont
      onMouseOver={() => setDownloadIconVis(true)}
      onMouseLeave={() => setDownloadIconVis(false)}
    >
      <ReportsNestedTableTileCont
        id="ReportsNestedTableTileCont"
        onClick={handleRedirectPrevent}
        to={`${RouteNames.Reports}/${id}?surveyType=${surveyType}`}
        is_anonymized={is_anonymized}
      >
        <ReportsNestedTableTileContentCont id="ReportsNestedTableTileContentCont">
          <ReportGroup id="ReportGroup">{group}</ReportGroup>
          <ReportsRegularNestedTableTileEl id="ReportsRegularNestedTableTileEl">
            {getReportType(type)}
          </ReportsRegularNestedTableTileEl>
          {hideAccess ? null : (
            <ReportsLowercaseNestedTableTileEl id="ReportsLowercaseNestedTableTileEl">
              {showAccessTooltip ? (
                <TooltipContainer id="TooltipCont">
                  <Tooltip
                    tooltipDesc={<TooltipList>{accessUsersList}</TooltipList>}
                    positionY="110%"
                    arrowPositionX="50%"
                    position="top"
                    width="fit-content"
                    background={is_anonymized ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 1)'}
                  >
                    <TooltipContent>
                      {users
                        .slice(0, 5)
                        .map(u => u?.name || u?.email || ' - ')
                        .join(', ')}
                      ...
                    </TooltipContent>
                  </Tooltip>
                </TooltipContainer>
              ) : (
                accessUsersString
              )}
            </ReportsLowercaseNestedTableTileEl>
          )}
          <ReportsRegularNestedTableTileEl id="ReportsRegularNestedTableTileEl">
            <ResponseRate
              mode={responsiveness.representativeness}
              percent={responsiveness.responseRate}
              responseCount={responsiveness.responseCount}
              maxResponseCount={responsiveness.participantCount}
            />
          </ReportsRegularNestedTableTileEl>
        </ReportsNestedTableTileContentCont>
      </ReportsNestedTableTileCont>
      {is_anonymized ? (
        <DownloadReportModal
          metadata={item}
          customButtonVisible={downloadIconVis}
          setCustomButtonVisible={setDownloadIconVis}
          customActionButton={
            <DownloadIconCont id="DownloadButton">
              <DownloadIcon />
            </DownloadIconCont>
          }
          customReportId={id}
        />
      ) : (
        <NoReport>{t('reports.noReportAvailable')}</NoReport>
      )}
    </ReportTileCont>
  );
};

export default ReportsNestedTableTile;
